/**
 * Rollback Netcode Engine
 *
 * Implements GGPO-style rollback networking:
 * 1. Both players run emulation locally at full speed
 * 2. Local input is applied immediately (no input lag for the local player)
 * 3. Remote input is predicted (repeat last known input)
 * 4. When actual remote input arrives and differs from prediction,
 *    rollback to the divergence frame, re-simulate forward with correct inputs
 * 5. Frame advantage tracking keeps players in sync
 * 6. Player 1 periodically sends authoritative state to Player 2
 */
export class RollbackEngine {
  constructor(emulator, localPlayer, opts = {}) {
    this.emu = emulator;
    this.localPlayer = localPlayer;        // 0 or 1
    this.remotePlayer = 1 - localPlayer;

    // Tuning
    this.inputDelay = opts.inputDelay ?? 2;
    this.maxRollbackFrames = opts.maxRollbackFrames ?? 8;
    this.maxFrameAdvantage = opts.maxFrameAdvantage ?? 10;

    // Frame counters
    const start = opts.startFrame ?? 0;
    this.currentFrame = start;
    this.lastConfirmedRemoteFrame = start - 1;

    // Input history (sparse maps: frame → bitmask)
    this.localInputs = {};
    this.remoteInputs = {};
    this.lastKnownRemoteInput = 0;

    // State ring buffer for rollback
    this.bufferSize = this.maxRollbackFrames + this.inputDelay + 2;
    this.stateBuffer = new Array(this.bufferSize).fill(null);

    // Sync stats
    this.rollbackCount = 0;
    this.rollbackFramesTotal = 0;
    this.frameAdvantage = 0;
    this.remoteFrameAdvantage = 0;

    // Desync detection
    this.syncInterval = opts.syncInterval ?? 60; // check every ~1 second
    this.lastSyncFrame = start;
    this.desyncDetected = false;
    this.desyncFrame = -1;
    this.onDesync = null;       // callback: (frame, localHash, remoteHash)
    this._pendingSyncHashes = {}; // frame → hash from remote
    this._localSyncHashes = {};   // frame → hash we computed locally

    // Force sync (P1 → P2 authoritative state transfer)
    this.onSendForceSync = null; // callback: (frame, state)
    this._forceSyncCooldown = 0;
    this.forceSyncInterval = opts.forceSyncInterval ?? 60; // ~1s at 60fps

    // Callback to send local input to network
    this.onSendInput = null;
    this.onSendSync = null;     // callback: (frame, hash)

    // Input queue from the raw input handler
    this._pendingLocalInput = 0;
  }

  /**
   * Adjust input delay at runtime. Clamped to [0, 6].
   */
  setInputDelay(delay) {
    this.inputDelay = Math.max(0, Math.min(6, delay));
    // Resize buffer if needed
    const needed = this.maxRollbackFrames + this.inputDelay + 2;
    if (needed > this.bufferSize) {
      this.bufferSize = needed;
      this.stateBuffer.length = this.bufferSize;
    }
  }

  /**
   * Set the current raw local input bitmask (called by input handler each tick).
   */
  setLocalInput(bitmask) {
    this._pendingLocalInput = bitmask;
  }

  /**
   * Receive remote input from the network.
   */
  receiveRemoteInput(frame, bitmask) {
    this.remoteInputs[frame] = bitmask;
    this.lastKnownRemoteInput = bitmask;

    if (frame > this.lastConfirmedRemoteFrame) {
      this.lastConfirmedRemoteFrame = frame;
    }

    // Check if we need rollback
    if (frame < this.currentFrame) {
      // We already simulated past this frame — check prediction
      const predicted = this._predictRemoteInput(frame);
      if (predicted !== bitmask) {
        this._rollback(frame);
      }
    }
  }

  /**
   * Receive frame advantage info from remote side (for speed sync).
   */
  receiveRemoteAdvantage(advantage) {
    this.remoteFrameAdvantage = advantage;
  }

  /**
   * Receive a sync hash from the remote player for desync detection.
   */
  receiveRemoteSyncHash(frame, hash) {
    this._pendingSyncHashes[frame] = hash;
    this._checkDesync(frame);
  }

  /**
   * Advance one frame. Returns false if we should wait (too far ahead).
   */
  advance() {
    // Calculate frame advantage
    this.frameAdvantage = this.currentFrame - this.lastConfirmedRemoteFrame;

    // Speed sync: if we're too far ahead, pause
    if (this._shouldWait()) {
      return false;
    }

    // The frame for which we're recording input (delayed)
    const inputFrame = this.currentFrame + this.inputDelay;

    // Store local input for the delayed frame
    this.localInputs[inputFrame] = this._pendingLocalInput;

    // Send input to the network
    if (this.onSendInput) {
      this.onSendInput(inputFrame, this._pendingLocalInput, this.frameAdvantage);
    }

    // Save state BEFORE simulating this frame
    this._saveState(this.currentFrame);

    // Gather inputs for this frame
    const localInput = this.localInputs[this.currentFrame] ?? 0;
    const remoteInput = this._getRemoteInput(this.currentFrame);

    // Apply to emulator
    this._applyInputs(localInput, remoteInput);
    this.emu.stepFrame();

    this.currentFrame++;

    // Periodic desync detection: emit state hash every N frames
    if (this.currentFrame - this.lastSyncFrame >= this.syncInterval) {
      this.lastSyncFrame = this.currentFrame;
      const hash = this.emu.stateFingerprint();
      this._localSyncHashes[this.currentFrame] = hash;
      if (this.onSendSync) {
        this.onSendSync(this.currentFrame, hash);
      }
      // Check against any pending remote hash for this frame
      this._checkDesync(this.currentFrame);
    }

    // Periodic authoritative sync: P1 pushes state to P2
    if (this.localPlayer === 0 &&
        this.currentFrame - this._forceSyncCooldown >= this.forceSyncInterval) {
      this._forceSyncCooldown = this.currentFrame;
      if (this.onSendForceSync) {
        this.onSendForceSync(this.currentFrame, this.emu.saveState());
      }
    }

    // Garbage collect old inputs/states
    this._gc();

    return true;
  }

  /**
   * Get current stats for the HUD.
   */
  getStats() {
    return {
      frame: this.currentFrame,
      frameAdvantage: this.frameAdvantage,
      rollbackCount: this.rollbackCount,
      avgRollback: this.rollbackCount > 0
        ? (this.rollbackFramesTotal / this.rollbackCount).toFixed(1)
        : 0,
      confirmedFrame: this.lastConfirmedRemoteFrame,
      delay: this.inputDelay,
      desync: this.desyncDetected,
      desyncFrame: this.desyncFrame,
    };
  }

  /**
   * Force a resync by reloading ROM state. Both players must call this.
   */
  clearDesync() {
    this.desyncDetected = false;
    this.desyncFrame = -1;
  }

  /**
   * Apply authoritative state from Player 1. Called on Player 2 only.
   */
  applyForceSync(frame, state) {
    this.emu.loadState(state);

    this.currentFrame = frame;
    this.lastSyncFrame = frame;
    this.lastConfirmedRemoteFrame = frame - 1;

    // Clear stale data from the old timeline
    this.localInputs = {};
    this.remoteInputs = {};
    this.stateBuffer = new Array(this.bufferSize).fill(null);
    this._pendingSyncHashes = {};
    this._localSyncHashes = {};

    // Reset stats
    this.rollbackCount = 0;
    this.rollbackFramesTotal = 0;

    // Clear desync
    this.desyncDetected = false;
    this.desyncFrame = -1;

    // Save the synced state as the first ring buffer entry
    this._saveState(this.currentFrame);
  }

  // ── Private ────────────────────────────────────────────

  _getRemoteInput(frame) {
    if (this.remoteInputs[frame] !== undefined) {
      return this.remoteInputs[frame];
    }
    return this._predictRemoteInput(frame);
  }

  _predictRemoteInput(frame) {
    // Walk backwards to find most recent known input
    for (let f = frame - 1; f >= Math.max(0, frame - this.maxRollbackFrames - this.inputDelay); f--) {
      if (this.remoteInputs[f] !== undefined) {
        return this.remoteInputs[f];
      }
    }
    return this.lastKnownRemoteInput;
  }

  _rollback(toFrame) {
    const framesBack = this.currentFrame - toFrame;
    if (framesBack <= 0 || framesBack > this.maxRollbackFrames + this.inputDelay) {
      return; // Can't rollback this far
    }

    this.rollbackCount++;
    this.rollbackFramesTotal += framesBack;

    // Load state at the divergence frame
    const loaded = this._loadState(toFrame);
    if (!loaded) return;

    // Re-simulate from toFrame to currentFrame
    const targetFrame = this.currentFrame;
    this.currentFrame = toFrame;

    while (this.currentFrame < targetFrame) {
      const local = this.localInputs[this.currentFrame] ?? 0;
      const remote = this._getRemoteInput(this.currentFrame);
      this._applyInputs(local, remote);
      this.emu.stepFrame();
      this._saveState(this.currentFrame);
      this.currentFrame++;
    }
  }

  _applyInputs(localBitmask, remoteBitmask) {
    this.emu.setButtons(this.localPlayer, localBitmask);
    this.emu.setButtons(this.remotePlayer, remoteBitmask);
  }

  _saveState(frame) {
    const idx = frame % this.bufferSize;
    this.stateBuffer[idx] = {
      frame,
      data: this.emu.saveState(),
    };
  }

  _loadState(frame) {
    const idx = frame % this.bufferSize;
    const entry = this.stateBuffer[idx];
    if (!entry || entry.frame !== frame) return false;
    this.emu.loadState(entry.data);
    return true;
  }

  _shouldWait() {
    // If remote player hasn't sent any input yet and we're past the initial delay, wait
    if (this.lastConfirmedRemoteFrame < 0 && this.currentFrame > this.inputDelay + 2) {
      return true;
    }
    // Limit how far ahead we can get
    if (this.frameAdvantage > this.maxFrameAdvantage) {
      return true;
    }
    return false;
  }

  /**
   * Compare local and remote state hashes at a given frame.
   * Both hashes must be for the SAME frame — local hash is stored
   * when we reach a sync frame, remote hash arrives via network.
   */
  _checkDesync(frame) {
    const remoteHash = this._pendingSyncHashes[frame];
    const localHash = this._localSyncHashes[frame];
    if (!remoteHash || !localHash) return; // Need both hashes for the same frame

    if (localHash !== remoteHash && !this.desyncDetected) {
      this.desyncDetected = true;
      this.desyncFrame = frame;
      if (this.onDesync) {
        this.onDesync(frame, localHash, remoteHash);
      }
      // P1 sends immediate corrective sync on desync
      if (this.localPlayer === 0) {
        if (this.onSendForceSync) {
          this._forceSyncCooldown = this.currentFrame;
          this.onSendForceSync(this.currentFrame, this.emu.saveState());
        }
        this.desyncDetected = false;
        this.desyncFrame = -1;
      }
    }

    // Clean up processed hashes
    delete this._pendingSyncHashes[frame];
    delete this._localSyncHashes[frame];
  }

  _gc() {
    const minFrame = this.currentFrame - this.bufferSize;
    for (const key of Object.keys(this.localInputs)) {
      if (parseInt(key) < minFrame) delete this.localInputs[key];
    }
    for (const key of Object.keys(this.remoteInputs)) {
      if (parseInt(key) < minFrame) delete this.remoteInputs[key];
    }
    // Clean stale sync hashes
    for (const key of Object.keys(this._pendingSyncHashes)) {
      if (parseInt(key) < minFrame) delete this._pendingSyncHashes[key];
    }
    for (const key of Object.keys(this._localSyncHashes)) {
      if (parseInt(key) < minFrame) delete this._localSyncHashes[key];
    }
  }
}
